import { Controller, useFormContext } from 'react-hook-form';
import { Box, Checkbox, FormControlLabel, FormInputText, InputLabel, Select } from '@hopskipdrive/react-components';
import * as Yup from 'yup';

import { FlexContent } from '../styles';
import { ISharedBillingFormType } from '../types';

export const SharedBillingForm = ({ useAccounts }: { useAccounts: () => Nullable<Account[]> }) => {
  const {
    watch,
    control,
    clearErrors,
    formState: { errors },
  } = useFormContext<ISharedBillingFormType>();
  const isSharedBillingRider = watch('isSharedBillingRider');
  const accounts = useAccounts();

  return (
    <Box display="flex" flexDirection="column" gap="1rem" alignSelf="stretch">
      <Box display="flex" flexWrap="wrap" flex="1" gap="2rem">
        <FlexContent>
          <FormControlLabel
            label={'This rider’s transportation costs are shared with another district'}
            labelPlacement="end"
            control={
              <Controller
                control={control}
                name="isSharedBillingRider"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    color="primary"
                    checked={value}
                    disabled={!accounts || accounts.length === 0}
                    sx={{ marginLeft: '0.5rem' }}
                    onChange={(e) => {
                      onChange(e.target.checked);
                      if (!e.target.checked) {
                        clearErrors(['sharedBillingRider']);
                      }
                    }}
                  />
                )}
              />
            }
          />
        </FlexContent>
        <FlexContent>
          <InputLabel htmlFor="residenceAccountId">Residence District</InputLabel>
          <Controller
            control={control}
            name="sharedBillingRider.residenceAccountId"
            render={({ field: { onChange, value } }) => {
              return (
                <Select
                  id="residenceAccountId"
                  options={accounts?.map((a) => ({ label: a.name, value: a.id })) || []}
                  value={(accounts?.find((a) => a.id === value) && value) || undefined}
                  setValue={onChange}
                  isLoading={!!value && !accounts}
                  renderInput={(params) => (
                    <FormInputText
                      {...params}
                      type="text"
                      disabled={(value && (!accounts || accounts.length === 0)) || !isSharedBillingRider}
                      id="residenceAccountId"
                      inputProps={{ ...params.inputProps, 'aria-required': !!isSharedBillingRider }}
                      variant="outlined"
                      data-testid="residenceAccountId"
                      placeholder="School District"
                      error={!!errors.sharedBillingRider?.residenceAccountId}
                      helperText={errors.sharedBillingRider?.residenceAccountId?.message}
                    />
                  )}
                />
              );
            }}
          />
        </FlexContent>
      </Box>
      <Box display="flex" flexWrap="wrap" flex="1" gap="2rem">
        <FlexContent>
          <InputLabel htmlFor="residenceAccountContactName">Contact Name for notifications</InputLabel>
          <Controller
            name="sharedBillingRider.residenceAccountContactName"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormInputText
                inputProps={{ 'aria-required': !!isSharedBillingRider }}
                id="residenceAccountContactName"
                className={
                  errors.sharedBillingRider?.residenceAccountContactName?.message &&
                  errors.sharedBillingRider?.residenceAccountContactName?.message.length > 100
                    ? 'long-error'
                    : ''
                }
                name="sharedBillingRider.residenceAccountContactName"
                value={value}
                variant="outlined"
                data-testid="residenceAccountContactName"
                fullWidth
                disabled={!isSharedBillingRider}
                placeholder="Name"
                error={!!errors.sharedBillingRider?.residenceAccountContactName}
                helperText={errors.sharedBillingRider?.residenceAccountContactName?.message}
                onChange={onChange}
              />
            )}
          />
        </FlexContent>
        <FlexContent>
          <InputLabel htmlFor="residenceAccountContactEmail">Contact Email</InputLabel>
          <Controller
            name="sharedBillingRider.residenceAccountContactEmail"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormInputText
                inputProps={{ 'aria-required': !!isSharedBillingRider }}
                id="residenceAccountContactEmail"
                value={value}
                name="sharedBillingRider.residenceAccountContactEmail"
                variant="outlined"
                data-testid="residenceAccountContactEmail"
                fullWidth
                placeholder="Email"
                type="email"
                disabled={!isSharedBillingRider}
                error={!!errors.sharedBillingRider?.residenceAccountContactEmail}
                helperText={errors.sharedBillingRider?.residenceAccountContactEmail?.message}
                onChange={onChange}
              />
            )}
          />
        </FlexContent>
      </Box>
    </Box>
  );
};

export function sharedBillingValidationSchema() {
  return Yup.object().shape({
    sharedBillingRider: Yup.object().when('isSharedBillingRider', {
      is: true,
      then: Yup.object().shape({
        residenceAccountContactEmail: Yup.string().required('Required Field'),
        residenceAccountContactName: Yup.string().required('Required Field'),
        residenceAccountId: Yup.number().nullable().required('Required Field'),
      }),
    }),
  });
}
